/**
 * App entry point.
 *
 * Import order is critical:
 * 1. Amplify.configure MUST be imported before any Amplify SDK usage (Pattern 4 — RESEARCH.md)
 * 2. Global CSS (index.css) must be imported before component styles
 */
import './lib/amplify'; // configure Amplify before rendering (RESEARCH.md Pattern 4)
import './index.css';

import * as Sentry from '@sentry/react';

// Frontend error tracking — no-op until VITE_SENTRY_DSN is provided at build time
// (GitHub env var / .env.local). Errors only, no performance tracing: keeps usage
// inside Sentry's free tier. Covers the web app AND both Capacitor shells (they
// run this same bundle). Dev builds stay un-instrumented.
if (import.meta.env.PROD && import.meta.env.VITE_SENTRY_DSN) {
  Sentry.init({
    dsn: import.meta.env.VITE_SENTRY_DSN,
    environment: import.meta.env.VITE_APP_ENV ?? 'production',
    tracesSampleRate: 0,
    sendDefaultPii: false,
  });
}

import { StrictMode } from 'react';
import { createRoot } from 'react-dom/client';
import { RouterProvider, createRouter } from '@tanstack/react-router';
import { QueryClient, QueryClientProvider, QueryCache } from '@tanstack/react-query';
import { registerSW } from 'virtual:pwa-register';
import { StatusBar, Style } from '@capacitor/status-bar';
import { Toaster } from '@/components/ui/sonner';
import { routeTree } from './routeTree.gen';
import { AuthSessionExpiredError } from '@/lib/api';
import { isRunningInNativeShell } from '@/lib/pwa';
import { registerNativeBackButton } from '@/lib/nativeBack';
import { registerNativeShortcuts } from '@/lib/nativeShortcut';
import { registerNativeGoogleAuth } from '@/lib/nativeGoogleAuth';
import { registerNativeScroll } from '@/lib/nativeScroll';
import { registerAnalytics } from '@/lib/analytics';

// Register the generated service worker. `vite-plugin-pwa` is configured with
// `injectRegister: false` (see vite.config.ts) — nothing registers the SW
// unless we call this ourselves. `immediate: true` is required; without it
// the SW is never registered. Skipped inside a future Capacitor native shell,
// which must never run this web SW (see lib/pwa.ts).
if (!isRunningInNativeShell()) {
  registerSW({ immediate: true });
}

// CAP-06: the app chrome is light (white/blue), so status-bar icons must be dark.
// Style.Light = "dark text for light backgrounds" per Capacitor's (confusingly
// named) enum. Native-gated; no-op on web. setOverlaysWebView/setBackgroundColor
// are deliberately NOT used — they no-op on Android 15+ (see 05-RESEARCH.md).
if (isRunningInNativeShell()) {
  StatusBar.setStyle({ style: Style.Light }).catch(() => {});
}

// Create TanStack Router instance with file-based route tree
const router = createRouter({ routeTree });

// CAP-06 / fix-260721: on iOS, WKWebView's own "scroll the page to reveal the focused input"
// behavior fights with the calendar's `position: fixed` header/dialog-overlay elements — the
// webview scrolls its outer document to bring a focused field (e.g. the Note textarea near
// the bottom of the Add-entry dialog) above the keyboard, but fixed-position ancestors don't
// stay pinned during that native scroll, so the header/dialog shift off-screen (05-04). The
// fix is to disable the WebView's own scroll — but ONLY on the calendar route, because doing
// it globally (as 05-04 originally did) also freezes normal document-flow pages like
// Categories/Settings, which then can't scroll at all. registerNativeScroll toggles it per
// route (self-gates on native iOS). See lib/nativeScroll.ts.
registerNativeScroll(router);

// CAP-05: Android hardware/gesture back-button precedence (dialog -> drawer ->
// history -> exit). Self-gates on native; unconditional call is safe on web.
registerNativeBackButton(router);

// iOS home-screen quick action ("Add entry"): opens the add-entry dialog when the
// app is launched/resumed via the long-press shortcut. Self-gates on native.
registerNativeShortcuts(router);

// Native Google sign-in: completes the system-browser OAuth flow when the browser
// deep-links back in via com.brokeornot.app://login. Must be registered at boot,
// not from the login route — the app may be cold-started by the deep link itself.
// Self-gates on native.
registerNativeGoogleAuth(router);

// GA4 page_view tracking — prod-only (VITE_GA_MEASUREMENT_ID set, PROD build),
// a true no-op everywhere else. Also covers both Capacitor shells (same bundle).
registerAnalytics(router);

// Register the router instance for type safety
declare module '@tanstack/react-router' {
  interface Register {
    router: typeof router;
  }
}

// TanStack Query client — server state management for API calls
const queryClient = new QueryClient({
  // When a query fails because the Cognito session expired/was invalid (e.g. stale
  // tokens for a deleted user — see debug session google-sso-budget-401), api.ts
  // has already cleared the broken session. Route the user back to sign-in instead
  // of leaving them on a half-authenticated shell showing a generic data error.
  queryCache: new QueryCache({
    onError: (error) => {
      if (error instanceof AuthSessionExpiredError) {
        void router.navigate({ to: '/' });
      }
    },
  }),
  defaultOptions: {
    queries: {
      staleTime: 1000 * 60, // 1 minute
      // Do not retry auth-expiry failures — the session is gone, retrying is futile.
      retry: (failureCount, error) =>
        error instanceof AuthSessionExpiredError ? false : failureCount < 1,
    },
  },
});

const rootElement = document.getElementById('root');
if (!rootElement) throw new Error('Root element not found');

createRoot(rootElement).render(
  <StrictMode>
    <QueryClientProvider client={queryClient}>
      <RouterProvider router={router} />
      <Toaster />
    </QueryClientProvider>
  </StrictMode>
);
